import { CircularProgress, Grid, Stack } from '@mui/material';
import CustomTooltip from 'common/components/CustomTooltip';
import { CALL_TO_ACTION } from 'common/constants/callToAction';
import AssetType from 'common/enums/assetTypes';
import StickerType from 'common/enums/stickersTypes';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import EmptyView from 'common/features/noData/EmptyView';
import useFlexBoxStyles from 'common/flexBox';
import LayoutSubForm from 'common/layouts/LayoutSubForm';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import EventFormAssetsItem from './EventFormAssetsItem';
import EventItemCarousel from './common/EventItemCarousel';

import EmptyTypes from 'common/enums/emptyTypes';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    selectedContent: {
        minHeight: theme.spacing(23),
        height: theme.spacing(23),
        [theme.breakpoints.up('xl')]: {
            minHeight: theme.spacing(29),
            height: theme.spacing(29),
        },
    },
    selectAssetsScroll: {
        height: 'calc(100% - 60px)',
        minHeight: 'calc(100% - 96px)',
    },
}));

const EventFormSelectAssets = (props) => {
    const {
        filter,
        search,
        assets,
        pending,
        selectedAssets,
        close,
        onConfirm,
        loadMore,
        hasMore,
        emptyType,
        emptyDescription,
        formAdd,
        sectionName,
        idProperty = 'id',
        emptyDescriptionNoAssets,
        allowOnlyOneMultimedia = false,
    } = props;

    const { t } = useTranslation();

    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();

    const isAuthorize = useAuthLicenseAddOns();

    const [showAssets, setShowAssets] = useState([]);
    const [selected, setSelected] = useState(selectedAssets || []);

    useEffect(() => {
        if (assets) {
            const showAssetsTemp = assets.filter(
                (a) => !selected.some((b) => (b[idProperty] || b.id) === (a[idProperty] || a.id)),
            );
            setShowAssets(showAssetsTemp);
        }
    }, [assets, selected, idProperty]);

    const addAssets = (id) => {
        const asset = assets.find((asset) => (asset[idProperty] || asset.id) === id);
        const selectedTemp = [asset, ...(!allowOnlyOneMultimedia ? selected : [])];
        setSelected(selectedTemp);
        const showAssetsTemp = assets.filter(
            (a) => !selectedTemp.some((b) => (b[idProperty] || b.id) === (a[idProperty] || a.id)),
        );
        resetStates(showAssetsTemp);
    };

    const deleteAsset = (asset) => {
        const index = selected.findIndex((b) => (b[idProperty] || b.id) === (asset[idProperty] || asset.id));
        const assetsTemp = [...selected.slice(0, index), ...selected.slice(index + 1)];
        setSelected(assetsTemp);
        const showAssetsTemp = [asset, ...showAssets];
        resetStates(showAssetsTemp);
    };

    const resetStates = (showAssetsTemp) => {
        setShowAssets(showAssetsTemp);
    };

    const done = (event) => {
        event.preventDefault();
        onConfirm(selected);
        close();
    };

    const renderAsset = (asset, disable = false, deleteButton = false, enableOnClick = false) => {
        return (
            <Grid key={asset[idProperty]} item xl={12 / 5} lg={3} st={4} lt={6} ls={12}>
                <CustomTooltip arrow disableHoverListener={!disable} title={t('common.utils.const.notAuthorize')}>
                    <EventFormAssetsItem
                        enableOnClick={enableOnClick && !disable}
                        onClick={() => addAssets(asset[idProperty])}
                        asset={asset}
                        deleteAsset={deleteAsset}
                        deleteButton={deleteButton}
                    />
                </CustomTooltip>
            </Grid>
        );
    };

    const getItems = ({ array, deleteButton, enableOnClick }) => {
        const authorize = isAuthorize(['customizable_posters_labels']);
        return array.map((asset, index) => {
            if (asset.type === CALL_TO_ACTION) {
                const disable = asset.callToActionType !== AssetType.PROMOTION && !authorize;
                return (
                    <React.Fragment key={index + 1}>
                        {renderAsset(asset, disable, deleteButton, enableOnClick)}
                    </React.Fragment>
                );
            }

            if (asset.type === AssetType.STICKER) {
                const disable = asset.stickerType !== StickerType.CHYRON && !authorize;
                return (
                    <React.Fragment key={index + 1}>
                        {renderAsset(asset, disable, deleteButton, enableOnClick)}
                    </React.Fragment>
                );
            }

            return (
                <React.Fragment key={index + 1}>
                    {renderAsset(asset, false, deleteButton, enableOnClick)}
                </React.Fragment>
            );
        });
    };

    return (
        <LayoutSubForm sectionName={sectionName} close={close} done={done} showCloseButton={false}>
            <Stack className={cx(classes.selectedContent, flexBox.relative)}>
                {Boolean(selected?.length) && (
                    <EventItemCarousel upcomingLength={selected?.length}>
                        <Grid container spacing={1} wrap="nowrap">
                            {getItems({ array: selected, deleteButton: true, enableOnClick: false })}
                        </Grid>
                    </EventItemCarousel>
                )}

                {!selected?.length && <EmptyView size="small" type={emptyType} description={emptyDescription} />}
            </Stack>

            <Stack direction="column" className={cx(flexBox.overflowXHidden, flexBox.grow1, flexBox.vH100)}>
                <Stack direction="column" className={cx(flexBox.scrollbarNone, flexBox.overflowXHidden, flexBox.h100)}>
                    {filter}

                    <Stack direction="column" className={cx(flexBox.overflowHidden, flexBox.h100, flexBox.relative)}>
                        {assets.length === 0 && pending && (
                            <Stack
                                direction="row"
                                justifyContent="center"
                                alignItems="center"
                                className={cx(flexBox.zIndex8, flexBox.h100, flexBox.w100, flexBox.absolute)}
                            >
                                <CircularProgress />
                            </Stack>
                        )}
                        <Stack
                            direction="column"
                            className={cx(
                                classes.selectAssetsScroll,
                                flexBox.scrollbarNone,
                                flexBox.overflowXHidden,
                                flexBox.grow1,
                            )}
                        >
                            <InfiniteScroll
                                pageStart={0}
                                loadMore={loadMore}
                                hasMore={hasMore}
                                loader={<CircularProgress key={0} />}
                                useWindow={false}
                            >
                                <Grid container spacing={1}>
                                    {formAdd || null}
                                    {getItems({ array: showAssets, deleteButton: false, enableOnClick: true })}
                                </Grid>
                            </InfiniteScroll>
                        </Stack>
                        {!assets?.length && !pending && (
                            <EmptyView
                                size="small"
                                noImage={true}
                                type={search ? EmptyTypes.EMPTY_SEARCH : ''}
                                description={emptyDescriptionNoAssets}
                            />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </LayoutSubForm>
    );
};

export default EventFormSelectAssets;
